import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import { CONFIRM_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminHeaderAction,
  AdminFormSecondaryAction,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminSupportingText,
  AdminFormDropdownChevron,
  AdminCompactFormPage,
  adminFormDetailInputClasses,
  adminFormTwoColGridClass,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import { buildSampleFormInventory } from '../../../data/formInventoryConfig';
import {
  CONDITION_MATCH_OPTIONS,
  CONDITION_OPERATOR_OPTIONS,
  CONDITION_SOURCE_OPTIONS,
  FORM_INVENTORY_CONFIGURATION_ROUTE,
  MESSAGES,
  TARGET_TYPE_OPTIONS,
  VALIDATION_MESSAGES,
  buildSampleFormInventoryConfigurationRules,
  conditionRequiresReference,
  conditionRequiresValue,
  createEmptyCondition,
  createEmptyEffect,
  createEmptyRule,
  effectRequiresValue,
  enrichRuleFromInventory,
  findRuleById,
  formatConditionListLabel,
  formatEffectsDisplay,
  formatTargetDisplay,
  getActiveForms,
  getConditionReferences,
  getConditionValueOptions,
  getConfigurableFieldsForForm,
  getFormById,
  getPermittedEffectsForTarget,
} from '../../../data/formInventoryConfigurationConfig';
import {
  assignNewConfigurationId,
  buildFormInventoryConfigurationActor,
  canAccessFormInventoryConfiguration,
  collectFieldErrors,
  hasValidationErrors,
  mergeRulesAfterConfirm,
  prepareRuleForSave,
} from '../../../data/formInventoryConfigurationWorkflow';

const ViewText = ({ value, mono = false }) => (
  <AdminDetailFieldValue className={`py-2 break-words${mono ? ' font-mono' : ''}`}>
    {value == null || value === '' ? '—' : String(value)}
  </AdminDetailFieldValue>
);

/** View → plain text. Add/Edit locked values → readonly field shell. */
const ReadOnlyField = ({ label, value, mono = false, isView = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {isView ? (
      <ViewText value={value} mono={mono} />
    ) : (
      <input
        type="text"
        value={value == null || value === '' ? '—' : String(value)}
        readOnly
        className={`${adminFormDetailInputClasses(true)}${mono ? ' font-mono' : ''}`}
      />
    )}
  </div>
);

const TextField = ({ label, value, onChange, required, error, placeholder, isView = false }) => {
  if (isView) {
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
        <ViewText value={value} />
      </div>
    );
  }
  return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>
        {label} {required ? <span className="text-alertred">*</span> : null}
      </AdminDetailFieldLabel>
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        className={adminFormDetailInputClasses(false, !!error)}
      />
      {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
    </div>
  );
};

const SimpleSelect = ({ label, value, options, onChange, required, error, placeholder, isView = false }) => {
  const selected = options.find((opt) => {
    const val = typeof opt === 'string' ? opt : opt.value;
    return val === value;
  });
  const display =
    typeof selected === 'string'
      ? selected
      : selected?.label || value || '—';

  if (isView) {
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
        <ViewText value={display} />
      </div>
    );
  }
  return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>
        {label} {required ? <span className="text-alertred">*</span> : null}
      </AdminDetailFieldLabel>
      <div className="relative">
        <select
          value={value || ''}
          onChange={(e) => onChange?.(e.target.value)}
          className={`${adminFormDetailInputClasses(false, !!error)} appearance-none pr-8`}
        >
          <option value="">{placeholder || 'Select'}</option>
          {options.map((opt) => {
            const val = typeof opt === 'string' ? opt : opt.value;
            const text = typeof opt === 'string' ? opt : opt.label;
            return (
              <option key={val} value={val}>
                {text}
              </option>
            );
          })}
        </select>
        <AdminFormDropdownChevron />
      </div>
      {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
    </div>
  );
};

const RadioGroup = ({ label, value, options, onChange, required, error, isView = false }) => {
  if (isView) {
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
        <ViewText value={value || '—'} />
      </div>
    );
  }
  return (
    <div className="space-y-2 w-full min-w-0">
      <AdminDetailFieldLabel>
        {label} {required ? <span className="text-alertred">*</span> : null}
      </AdminDetailFieldLabel>
      <div className="flex flex-wrap gap-4">
        {options.map((opt) => (
          <label key={opt} className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={label}
              checked={value === opt}
              onChange={() => onChange?.(opt)}
              className="accent-primaryblue"
            />
            <AdminDetailFieldValue className="!mb-0">{opt}</AdminDetailFieldValue>
          </label>
        ))}
      </div>
      {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
    </div>
  );
};

const ConfirmModal = ({ title, children, confirmLabel, onConfirm, onClose }) => (
  <div
    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
    onClick={onClose}
    role="presentation"
  >
    <div
      className={`bg-white rounded-xl shadow-lg ${CONFIRM_POPUP_CONTENT_WIDTH_CLASS} max-h-[90vh] overflow-y-auto`}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <div className="relative px-5 pt-5 pb-3 border-b border-lightgray">
        <AdminPanelTitle className="text-offblack text-center">{title}</AdminPanelTitle>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 text-offblack hover:text-primaryblue"
          aria-label="Close"
        >
          <i className="fi fi-rs-cross text-sm" aria-hidden />
        </button>
      </div>
      <div className="px-5 py-4 space-y-3">{children}</div>
      <div className="px-5 pb-5 flex flex-wrap justify-end gap-3">
        <AdminFormSecondaryAction type="button" onClick={onClose}>
          Cancel
        </AdminFormSecondaryAction>
        <AdminHeaderAction type="button" onClick={onConfirm}>
          {confirmLabel}
        </AdminHeaderAction>
      </div>
    </div>
  </div>
);

const FormInventoryConfigurationRuleDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { configId } = useParams();
  const { auth } = useAuth();
  const canAccess = canAccessFormInventoryConfiguration(auth);
  const actor = useMemo(() => buildFormInventoryConfigurationActor(auth), [auth]);

  const isAdd = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');
  const isView = !isAdd && !isEdit;

  const [forms] = useState(() => location.state?.forms ?? buildSampleFormInventory());
  const [allRules, setAllRules] = useState(() => {
    const incoming = location.state?.rules;
    if (Array.isArray(incoming)) return incoming.map((rule) => enrichRuleFromInventory(rule, forms));
    return buildSampleFormInventoryConfigurationRules(forms);
  });

  const resolvedRule = useMemo(() => {
    if (isAdd) return createEmptyRule();
    const fromState = location.state?.rule;
    if (fromState?.id) return enrichRuleFromInventory(fromState, forms);
    return enrichRuleFromInventory(findRuleById(allRules, configId) || createEmptyRule(), forms);
  }, [isAdd, location.state?.rule, allRules, configId, forms]);

  const [formData, setFormData] = useState(() => cloneForEdit(resolvedRule));
  const [errors, setErrors] = useState({});
  const [confirmSave, setConfirmSave] = useState(false);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    setFormData(cloneForEdit(resolvedRule));
    setErrors({});
  }, [resolvedRule]);

  const selectedForm = useMemo(
    () => getFormById(forms, formData.targetFormId),
    [forms, formData.targetFormId],
  );

  const formOptions = useMemo(() => {
    const active = getActiveForms(forms).map((form) => ({
      value: form.id,
      label: `${form.id} — ${form.formName}`,
    }));
    if (
      formData.targetFormId &&
      !active.some((opt) => opt.value === formData.targetFormId) &&
      selectedForm
    ) {
      return [
        { value: selectedForm.id, label: `${selectedForm.id} — ${selectedForm.formName}` },
        ...active,
      ];
    }
    return active;
  }, [forms, formData.targetFormId, selectedForm]);

  const modeOptions = selectedForm?.modes || [];
  const fieldOptions = useMemo(() => {
    const fields = getConfigurableFieldsForForm(selectedForm, {
      includeInactiveSaved: isEdit || isView,
      savedFieldId: formData.targetFieldId,
    });
    return fields.map((field) => ({
      value: field.id,
      label: `${field.id} — ${field.fieldLabel}`,
    }));
  }, [selectedForm, formData.targetFieldId, isEdit, isView]);

  const selectedField = useMemo(
    () => (selectedForm?.fields || []).find((field) => field.id === formData.targetFieldId) || null,
    [selectedForm, formData.targetFieldId],
  );

  const permittedEffects = useMemo(
    () => getPermittedEffectsForTarget(formData.targetType, selectedForm, selectedField),
    [formData.targetType, selectedForm, selectedField],
  );

  const updateField = (key, value) => {
    setFormData((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'targetType') {
        next.targetMode = '';
        next.targetFieldId = '';
        next.targetFieldLabel = '';
        next.targetFieldInternalKey = '';
        next.effects = [createEmptyEffect()];
      }
      if (key === 'targetFormId') {
        const form = getFormById(forms, value);
        next.owningModule = form?.owningModule || '';
        next.targetFormName = form?.formName || '';
        next.targetMode = '';
        next.targetFieldId = '';
        next.targetFieldLabel = '';
        next.targetFieldInternalKey = '';
      }
      if (key === 'targetFieldId') {
        const field = (selectedForm?.fields || []).find((item) => item.id === value);
        next.targetFieldLabel = field?.fieldLabel || '';
        next.targetFieldInternalKey = field?.fieldKey || field?.internalFieldKey || '';
        next.effects = [createEmptyEffect()];
      }
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const updateCondition = (index, key, value) => {
    setFormData((prev) => {
      const conditions = [...(prev.conditions || [])];
      const current = { ...conditions[index], [key]: value };
      if (key === 'source') {
        current.reference = '';
        current.operator = '';
        current.value = '';
        current.values = [];
        delete current.displayLabel;
      }
      if (key === 'reference' || key === 'operator') {
        current.value = '';
        current.values = [];
        delete current.displayLabel;
      }
      conditions[index] = current;
      return { ...prev, conditions };
    });
  };

  const addCondition = () => {
    setFormData((prev) => ({
      ...prev,
      conditions: [...(prev.conditions || []), createEmptyCondition()],
    }));
  };

  const removeCondition = (index) => {
    setFormData((prev) => {
      if ((prev.conditions || []).length <= 1) {
        setErrors((errs) => ({ ...errs, conditions: VALIDATION_MESSAGES.finalConditionRemoved }));
        return prev;
      }
      return {
        ...prev,
        conditions: prev.conditions.filter((_, i) => i !== index),
      };
    });
  };

  const updateEffect = (index, key, value) => {
    setFormData((prev) => {
      const effects = [...(prev.effects || [])];
      const current = { ...effects[index], [key]: value };
      if (key === 'effectType') {
        current.effectValue = '';
        current.effectValues = [];
      }
      effects[index] = current;
      return { ...prev, effects };
    });
  };

  const addEffect = () => {
    setFormData((prev) => ({
      ...prev,
      effects: [...(prev.effects || []), createEmptyEffect()],
    }));
  };

  const removeEffect = (index) => {
    setFormData((prev) => {
      if ((prev.effects || []).length <= 1) {
        setErrors((errs) => ({ ...errs, effects: VALIDATION_MESSAGES.finalEffectRemoved }));
        return prev;
      }
      return {
        ...prev,
        effects: prev.effects.filter((_, i) => i !== index),
      };
    });
  };

  const handleSaveClick = () => {
    const fieldErrors = collectFieldErrors(formData, allRules, forms, isAdd ? undefined : formData.id);
    setErrors(fieldErrors);
    if (hasValidationErrors(fieldErrors)) {
      setFeedback('');
      return;
    }
    setConfirmSave(true);
  };

  const handleConfirmSave = () => {
    const prepared = prepareRuleForSave(formData, forms, actor, { isCreate: isAdd });
    const withId = isAdd ? assignNewConfigurationId(allRules, prepared) : prepared;
    const nextRules = mergeRulesAfterConfirm(allRules, withId, { isCreate: isAdd });
    setAllRules(nextRules);
    setConfirmSave(false);
    navigate(FORM_INVENTORY_CONFIGURATION_ROUTE, {
      state: {
        rules: nextRules,
        forms,
        saveSuccess: isAdd
          ? `${withId.ruleName} saved as ${withId.id}.`
          : `${withId.ruleName} updated.`,
      },
    });
  };

  if (!canAccess) {
    return (
      <AdminLayout>
        <AdminPageTitle>Form Inventory</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray mt-4">
          You do not have permission to view Form Inventory configuration.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  if (!isAdd && !resolvedRule?.id && !findRuleById(allRules, configId)) {
    return (
      <AdminLayout>
        <AdminPageTitle>Form Inventory</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray mt-4">
          The requested Form Inventory rule could not be located.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  const pageTitle = isAdd ? 'Add Form' : isEdit ? 'Edit Form' : 'Form Detail';

  const enrichedPreview = enrichRuleFromInventory(formData, forms);
  const repeatingFieldsClass = isView ? adminFormTwoColGridClass : 'space-y-6';

  const setupFields = (
    <>
      {!isAdd ? (
        <ReadOnlyField
          label="Configuration ID"
          value={formData.id || '—'}
          mono
          isView={isView}
        />
      ) : null}

      <TextField
        label="Rule Name"
        value={formData.ruleName}
        onChange={(value) => updateField('ruleName', value)}
        required
        error={errors.ruleName}
        placeholder="Enter a unique rule name"
        isView={isView}
      />

      <ReadOnlyField
        label="Owning Module"
        value={
          formData.owningModule ||
          (formData.targetFormId ? '—' : 'Populated after Target Form selection')
        }
        isView={isView}
      />

      <RadioGroup
        label="Target Type"
        value={formData.targetType}
        options={TARGET_TYPE_OPTIONS}
        onChange={(value) => updateField('targetType', value)}
        required
        error={errors.targetType}
        isView={isView}
      />

      <SimpleSelect
        label="Target Form"
        value={formData.targetFormId}
        options={formOptions}
        onChange={(value) => updateField('targetFormId', value)}
        required
        error={errors.targetForm}
        placeholder="Search Form ID or Form Name"
        isView={isView}
      />

      {formData.targetType === 'Specific Form Mode' ? (
        <SimpleSelect
          label="Target Mode"
          value={formData.targetMode}
          options={modeOptions}
          onChange={(value) => updateField('targetMode', value)}
          required
          error={errors.targetMode}
          placeholder="Select a fixed Form Mode"
          isView={isView}
        />
      ) : null}

      {formData.targetType === 'Specific Form Field' ? (
        <SimpleSelect
          label="Target Form Field"
          value={formData.targetFieldId}
          options={fieldOptions}
          onChange={(value) => updateField('targetFieldId', value)}
          required
          error={errors.targetField}
          placeholder="Search Form Field ID, Label or Internal Field Key"
          isView={isView}
        />
      ) : null}

      <RadioGroup
        label="Condition Match"
        value={formData.conditionMatch}
        options={CONDITION_MATCH_OPTIONS}
        onChange={(value) => updateField('conditionMatch', value)}
        required
        error={errors.conditionMatch}
        isView={isView}
      />
    </>
  );

  const conditionsBlock = (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <AdminPanelTitle className="text-offblack !mb-0">Conditions</AdminPanelTitle>
        {!isView ? (
          <AdminFormSecondaryAction type="button" onClick={addCondition}>
            Add Condition
          </AdminFormSecondaryAction>
        ) : null}
      </div>
      {errors.conditions ? <AdminFormFieldError>{errors.conditions}</AdminFormFieldError> : null}

      {(formData.conditions || []).map((condition, index) => {
        const references = getConditionReferences(condition.source, selectedForm);
        const valueOptions = getConditionValueOptions(condition.source, condition.reference);
        const showReference = conditionRequiresReference(condition.source);
        const showValue = conditionRequiresValue(condition.operator);
        const multiValue = condition.operator === 'Is One Of' || condition.operator === 'Is Not One Of';
        return (
          <div key={condition.id || index} className="space-y-4">
            <AdminDetailFieldValue className="text-offblack">
              Condition {index + 1}
            </AdminDetailFieldValue>

            <div className={repeatingFieldsClass}>
              <SimpleSelect
                label="Condition Source"
                value={condition.source}
                options={CONDITION_SOURCE_OPTIONS}
                onChange={(value) => updateCondition(index, 'source', value)}
                required
                error={errors[`condition_${index}_source`]}
                placeholder="Select the source evaluated by this condition"
                isView={isView}
              />

              {showReference ? (
                <SimpleSelect
                  label="Condition Reference"
                  value={condition.reference}
                  options={references}
                  onChange={(value) => updateCondition(index, 'reference', value)}
                  required
                  error={errors[`condition_${index}_reference`]}
                  placeholder="Select the field or saved configuration result"
                  isView={isView}
                />
              ) : null}

              <SimpleSelect
                label="Condition Operator"
                value={condition.operator}
                options={CONDITION_OPERATOR_OPTIONS}
                onChange={(value) => updateCondition(index, 'operator', value)}
                required
                error={errors[`condition_${index}_operator`]}
                placeholder="Select the comparison operator"
                isView={isView}
              />

              {showValue ? (
                multiValue ? (
                  <TextField
                    label="Condition Value"
                    value={
                      Array.isArray(condition.values) && condition.values.length
                        ? condition.values.join(', ')
                        : condition.value || ''
                    }
                    onChange={(value) => {
                      const values = String(value || '')
                        .split(',')
                        .map((part) => part.trim())
                        .filter(Boolean);
                      setFormData((prev) => {
                        const conditions = [...(prev.conditions || [])];
                        conditions[index] = {
                          ...conditions[index],
                          value: values.join(', '),
                          values,
                        };
                        delete conditions[index].displayLabel;
                        return { ...prev, conditions };
                      });
                    }}
                    required
                    error={errors[`condition_${index}_value`]}
                    placeholder="Enter or select the expected value"
                    isView={isView}
                  />
                ) : valueOptions.length ? (
                  <SimpleSelect
                    label="Condition Value"
                    value={condition.value}
                    options={valueOptions}
                    onChange={(value) => updateCondition(index, 'value', value)}
                    required
                    error={errors[`condition_${index}_value`]}
                    placeholder="Enter or select the expected value"
                    isView={isView}
                  />
                ) : (
                  <TextField
                    label="Condition Value"
                    value={condition.value}
                    onChange={(value) => updateCondition(index, 'value', value)}
                    required
                    error={errors[`condition_${index}_value`]}
                    placeholder="Enter or select the expected value"
                    isView={isView}
                  />
                )
              ) : null}
            </div>

            {!isView ? (
              <div className="flex flex-wrap gap-3">
                <AdminFormSecondaryAction type="button" onClick={() => removeCondition(index)}>
                  Remove from Rule
                </AdminFormSecondaryAction>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  const effectsBlock = (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <AdminPanelTitle className="text-offblack !mb-0">Effects</AdminPanelTitle>
        {!isView ? (
          <AdminFormSecondaryAction type="button" onClick={addEffect}>
            Add Effect
          </AdminFormSecondaryAction>
        ) : null}
      </div>
      {errors.effects ? <AdminFormFieldError>{errors.effects}</AdminFormFieldError> : null}
      {errors.overlap ? <AdminFormFieldError>{errors.overlap}</AdminFormFieldError> : null}

      {(formData.effects || []).map((effect, index) => {
        const needsValue = effectRequiresValue(effect.effectType);
        const selectable =
          selectedField?.fixedFormValues?.length > 0
            ? selectedField.fixedFormValues
            : [];
        return (
          <div key={effect.id || index} className="space-y-4">
            <AdminDetailFieldValue className="text-offblack">
              Effect {index + 1}
            </AdminDetailFieldValue>

            <div className={repeatingFieldsClass}>
              <SimpleSelect
                label="Effect Type"
                value={effect.effectType}
                options={permittedEffects}
                onChange={(value) => updateEffect(index, 'effectType', value)}
                required
                error={errors[`effect_${index}_type`]}
                placeholder="Select the behaviour applied when the conditions match"
                isView={isView}
              />

              {needsValue ? (
                selectable.length ? (
                  <SimpleSelect
                    label="Effect Value"
                    value={effect.effectValue}
                    options={selectable}
                    onChange={(value) => updateEffect(index, 'effectValue', value)}
                    required
                    error={errors[`effect_${index}_value`]}
                    placeholder="Enter or select the value applied by the effect"
                    isView={isView}
                  />
                ) : (
                  <TextField
                    label="Effect Value"
                    value={effect.effectValue}
                    onChange={(value) => updateEffect(index, 'effectValue', value)}
                    required
                    error={errors[`effect_${index}_value`]}
                    placeholder="Enter or select the value applied by the effect"
                    isView={isView}
                  />
                )
              ) : null}
            </div>

            {!isView ? (
              <div className="flex flex-wrap gap-3">
                <AdminFormSecondaryAction type="button" onClick={() => removeEffect(index)}>
                  Remove from Rule
                </AdminFormSecondaryAction>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  const saveModal = confirmSave ? (
    <ConfirmModal
      title={MESSAGES.saveConfirmTitle}
      confirmLabel={MESSAGES.saveConfirmLabel}
      onConfirm={handleConfirmSave}
      onClose={() => setConfirmSave(false)}
    >
      <div className="space-y-2">
        <AdminSupportingText className="text-offblack mb-0">
          <span className="font-medium">Rule Name:</span> {formData.ruleName}
        </AdminSupportingText>
        <AdminSupportingText className="text-offblack mb-0">
          <span className="font-medium">Target:</span> {formatTargetDisplay(enrichedPreview)}
        </AdminSupportingText>
        <AdminSupportingText className="text-offblack mb-0">
          <span className="font-medium">Conditions:</span>{' '}
          {(formData.conditions || []).map(formatConditionListLabel).join('; ')}
        </AdminSupportingText>
        <AdminSupportingText className="text-offblack mb-0">
          <span className="font-medium">Effects:</span>{' '}
          {formatEffectsDisplay(formData.effects).join(', ')}
        </AdminSupportingText>
      </div>
    </ConfirmModal>
  ) : null;

  if (isView) {
    return (
      <AdminLayout>
        <div className="min-w-0">
          <div className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
            <AdminPageTitle className="!mb-0">{pageTitle}</AdminPageTitle>
          </div>

          {feedback ? (
            <AdminSupportingText className="text-alertred mb-4">{feedback}</AdminSupportingText>
          ) : null}

          <div className={`space-y-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
            <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
              <div className="space-y-4">
                <AdminPanelTitle className="text-offblack mb-4">Rule Setup</AdminPanelTitle>
                <div className={adminFormTwoColGridClass}>
                  {!isAdd ? (
                    <ReadOnlyField
                      label="Configuration ID"
                      value={formData.id || '—'}
                      mono
                      isView
                    />
                  ) : null}
                  <TextField
                    label="Rule Name"
                    value={formData.ruleName}
                    isView
                  />
                  <ReadOnlyField
                    label="Owning Module"
                    value={
                      formData.owningModule ||
                      (formData.targetFormId ? '—' : 'Populated after Target Form selection')
                    }
                    isView
                  />
                  <div className="sm:col-span-2">
                    <RadioGroup
                      label="Target Type"
                      value={formData.targetType}
                      options={TARGET_TYPE_OPTIONS}
                      isView
                    />
                  </div>
                  <SimpleSelect
                    label="Target Form"
                    value={formData.targetFormId}
                    options={formOptions}
                    isView
                  />
                  {formData.targetType === 'Specific Form Mode' ? (
                    <SimpleSelect
                      label="Target Mode"
                      value={formData.targetMode}
                      options={modeOptions}
                      isView
                    />
                  ) : null}
                  {formData.targetType === 'Specific Form Field' ? (
                    <SimpleSelect
                      label="Target Form Field"
                      value={formData.targetFieldId}
                      options={fieldOptions}
                      isView
                    />
                  ) : null}
                  <div className="sm:col-span-2">
                    <RadioGroup
                      label="Condition Match"
                      value={formData.conditionMatch}
                      options={CONDITION_MATCH_OPTIONS}
                      isView
                    />
                  </div>
                </div>
              </div>
            </DashboardMainContainer>

            <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
              {conditionsBlock}
            </DashboardMainContainer>

            <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
              {effectsBlock}
            </DashboardMainContainer>

            {!isAdd ? (
              <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
                <div className="space-y-4">
                  <AdminPanelTitle className="text-offblack mb-4">Status</AdminPanelTitle>
                  <div className={adminFormTwoColGridClass}>
                    <ReadOnlyField
                      label="Rule Status"
                      value={enrichedPreview.ruleStatus || '—'}
                      isView
                    />
                    <ReadOnlyField
                      label="Evaluation Status"
                      value={enrichedPreview.evaluationStatus || '—'}
                      isView
                    />
                  </div>
                </div>
              </DashboardMainContainer>
            ) : null}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={pageTitle}
        onSubmit={handleSaveClick}
        saveLabel="Save & Confirm"
        beforeForm={
          feedback ? (
            <AdminSupportingText className="text-alertred mb-4">{feedback}</AdminSupportingText>
          ) : null
        }
        afterForm={saveModal}
      >
        {setupFields}
        {conditionsBlock}
        {effectsBlock}
        {!isAdd ? (
          <>
            <ReadOnlyField
              label="Rule Status"
              value={enrichedPreview.ruleStatus || '—'}
              isView={false}
            />
            <ReadOnlyField
              label="Evaluation Status"
              value={enrichedPreview.evaluationStatus || '—'}
              isView={false}
            />
          </>
        ) : null}
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

function cloneForEdit(rule) {
  return JSON.parse(JSON.stringify(rule || createEmptyRule()));
}

export default FormInventoryConfigurationRuleDetail;
